import { format } from 'date-fns';
import { FileCheck2, Goal, PencilRuler, Table } from 'lucide-react';
import { Card } from 'primereact/card';
import { FC, ReactNode } from 'react';
import { getDateByUserTimezone } from '../../businessLogicService/getDateByUserTimezone';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import TeacherDashboardInfoIcon, { InfoType } from './TeacherDashboardInfoIcon';
import { Button } from 'primereact/button';

interface Props {
  type: TeacherDashboardWidgetType;
  miamiBrand?: boolean;
  noHeader?: boolean;
  exitTicketSiteAdmin?: boolean;
  action?: ReactNode;
}
export enum TeacherDashboardWidgetType {
  TODAY_SKILL_CHALLENGE,
  WEEKLY_PACE,
  SKILL_CHALLENGE_ATTEMPTS,
  FOUNDATION,
  EXIT_SLIP,
}

const data = {
  [TeacherDashboardWidgetType.TODAY_SKILL_CHALLENGE]: {
    title: "Today's Skill Challenges",
    icon: <PencilRuler size={30} className="tw-text-stone-500" />,
    subtitle: 'No skill challenge yet',
    subtitle2: 'Data will appear as students complete skill challenges',
    subtitle2Alternative: '',
  },
  [TeacherDashboardWidgetType.WEEKLY_PACE]: {
    title: 'Weekly Pace',
    icon: <Goal size={30} className="tw-text-stone-500" />,
    subtitle: 'No pace data yet',
    subtitle2: 'Data will appear as students complete skill challenges',
    subtitle2Alternative: '',
  },
  [TeacherDashboardWidgetType.SKILL_CHALLENGE_ATTEMPTS]: {
    title: 'Skill Challenges attempted per student',
    icon: <Table size={30} className="tw-text-stone-500" />,
    subtitle: 'No attempts made yet',
    subtitle2: 'Data will appear as students complete skill challenges',
    subtitle2Alternative: '',
  },
  [TeacherDashboardWidgetType.FOUNDATION]: {
    title: 'Foundations/RTI Monitoring',
    icon: <PencilRuler size={30} className="tw-text-stone-500" />,
    subtitle: 'No skill challenge yet',
    subtitle2: 'Data will appear as students complete skill challenges',
    subtitle2Alternative: '',
  },
  [TeacherDashboardWidgetType.EXIT_SLIP]: {
    title: 'Exit Ticket',
    icon: <FileCheck2 size={30} className="tw-text-stone-500" />,
    subtitle: 'No exit tickets',
    subtitle2: 'Data will appear as students complete exit tickets',
    subtitle2Alternative: 'Select a class to see its exit tickets',
  },
};

const TeacherDashboardWidgetEmpty: FC<Props> = ({
  type,
  miamiBrand = false,
  noHeader = false,
  exitTicketSiteAdmin = false,
  action,
}) => {
  const userRedux = useAppSelector(selectUser);
  const config = data[type];

  return (
    <Card
      header={
        !noHeader && (
          <div className="tw-w-full tw-p-2 tw-flex tw-justify-start tw-items-center tw-gap-3 tw-border-b-[2px] tw-border-t-[0px] tw-border-x-[0px] tw-border-neutral-100 tw-border-solid">
            <h2 className="tw-text-lg tw-font-bold tw-not-italic">{config.title}</h2>
            {type === TeacherDashboardWidgetType.SKILL_CHALLENGE_ATTEMPTS ? (
              <div
                className="flex align-items-center justify-content-end xl:col-4 md:col-6"
                id="SkillChallengesWeekPicker"
              >
                <Button
                  style={{
                    borderRadius: '1rem 0 0 1rem',
                    color: '#808080',
                    borderColor: '#808080',
                    height: 'stretch',
                  }}
                  icon="pi pi-angle-left"
                  outlined
                  disabled
                  aria-label={'Previous week'}
                />
                <label
                  className="tw-font-atkinson"
                  style={{ border: '1px solid', borderColor: '#808080', color: '#808080', padding: '0.5rem' }}
                >
                  04/08/2026 - 04/14/2026
                </label>
                <Button
                  style={{
                    borderRadius: '0 1rem 1rem 0',
                    color: '#808080',
                    borderColor: '#808080',
                    height: 'stretch',
                  }}
                  icon="pi pi-angle-right"
                  outlined
                  disabled
                  aria-label={'Next week'}
                />
              </div>
            ) : (
              <p className="tw-text-neutral-600 tw-font-atkinson tw-text-base tw-m-0">
                {format(getDateByUserTimezone(userRedux?.activeTimeZone || '').zonedDate, 'P')}
              </p>
            )}
            {miamiBrand && type === TeacherDashboardWidgetType.FOUNDATION && (
              <TeacherDashboardInfoIcon infoType={InfoType.DAILY} />
            )}
            {miamiBrand && type === TeacherDashboardWidgetType.WEEKLY_PACE && (
              <TeacherDashboardInfoIcon infoType={InfoType.WEEKLY} />
            )}
          </div>
        )
      }
      className="tw-h-full nc_block tw-p-0 tw-my-4 !tw-shadow-none tw-w-full"
    >
      <div className="tw-w-full tw-p-8">
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
          <span className="tw-flex tw-p-4 tw-bg-stone-50 tw-rounded-full !tw-rotate-34">{config.icon}</span>
          <p className="tw-text-gray-600 tw-text-2xl tw-font-atkinson tw-font-bold tw-mb-2">{config.subtitle}</p>
          <p className="tw-text-gray-500 tw-text-sm tw-font-atkinson">
            {!exitTicketSiteAdmin ? config.subtitle2 : config.subtitle2Alternative}
          </p>
        </div>
        {action && <div className="tw-flex tw-justify-end">{action}</div>}
      </div>
    </Card>
  );
};

export default TeacherDashboardWidgetEmpty;
